import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

// ─────────────────────────────────────────────
// useInsights
// Lädt habit_history der letzten 30 Tage und
// berechnet alle Statistiken client-seitig.
// ─────────────────────────────────────────────
export function useInsights(userId, allHabits = []) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || allHabits.length === 0) return;
    load();
  }, [userId, allHabits.length]);

  async function load() {
    setLoading(true);
    const since = new Date();
    since.setDate(since.getDate() - 30);

    const { data: rows } = await supabase
      .from("habit_history")
      .select("date, status, done_habits, progress")
      .eq("user_id", userId)
      .gte("date", since.toISOString().slice(0, 10))
      .order("date", { ascending: true });

    if (!rows) { setLoading(false); return; }

    setData(compute(rows, allHabits));
    setLoading(false);
  }

  return { insights: data, loading, reload: load };
}

// ─────────────────────────────────────────────
// BERECHNUNGEN
// ─────────────────────────────────────────────
function compute(rows, allHabits) {
  const today = new Date().toISOString().slice(0, 10);
  const habitIds = allHabits.map((h) => h.id);

  // Map: date → row
  const byDate = {};
  rows.forEach((r) => { byDate[r.date] = r; });

  // ── Letzte 7 Tage für Wochengrafik ──────────
  const last7 = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const row = byDate[key];
    last7.push({
      date: key,
      label: d.toLocaleDateString("de-DE", { weekday: "short" }).slice(0, 2),
      isToday: key === today,
      progress: row?.progress || 0,
      status: row?.status || "empty",
      doneCount: (row?.done_habits || []).length,
      totalCount: allHabits.length,
    });
  }

  // ── Completion Rate (30 Tage) ────────────────
  const activeDays = rows.filter((r) => r.done_habits?.length > 0);
  const completedDays = rows.filter((r) => r.status === "complete");
  const completionRate = activeDays.length > 0
    ? Math.round((completedDays.length / activeDays.length) * 100)
    : 0;

  // ── Beste Streak ever ────────────────────────
  let bestStreak = 0, currentRun = 0;
  const sortedRows = [...rows].sort((a, b) => a.date.localeCompare(b.date));
  for (const row of sortedRows) {
    if (row.status === "complete" || row.status === "freeze") {
      currentRun++;
      bestStreak = Math.max(bestStreak, currentRun);
    } else if (row.status !== "grace") {
      currentRun = 0;
    }
  }

  // ── Habit-Streaks (pro Habit) ────────────────
  const habitStreaks = {};
  for (const habitId of habitIds) {
    let streak = 0;
    const sortedDesc = [...sortedRows].reverse();
    for (const row of sortedDesc) {
      if (row.date === today) continue; // heute zählt erst abends
      if ((row.done_habits || []).includes(habitId)) {
        streak++;
      } else {
        break;
      }
    }
    // Heute miteinbeziehen wenn schon erledigt
    const todayRow = byDate[today];
    if (todayRow && (todayRow.done_habits || []).includes(habitId)) {
      streak++;
    }
    habitStreaks[habitId] = streak;
  }

  // ── Top Habits (am konsistentesten) ──────────
  const habitCounts = {};
  for (const row of rows) {
    for (const hId of (row.done_habits || [])) {
      habitCounts[hId] = (habitCounts[hId] || 0) + 1;
    }
  }

  const habitStats = allHabits.map((h) => ({
    id: h.id,
    label: h.label,
    count: habitCounts[h.id] || 0,
    streak: habitStreaks[h.id] || 0,
    rate: rows.length > 0 ? Math.round(((habitCounts[h.id] || 0) / rows.length) * 100) : 0,
  })).sort((a, b) => b.count - a.count);

  const topHabit    = habitStats[0] || null;
  const missedHabit = [...habitStats].sort((a, b) => a.count - b.count)[0] || null;

  // ── Aktivste Tage ────────────────────────────
  const dayOfWeekCounts = Array(7).fill(0);
  const dayOfWeekNames  = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  for (const row of completedDays) {
    const dow = new Date(row.date + "T12:00:00").getDay();
    dayOfWeekCounts[dow]++;
  }
  const bestDayIdx = dayOfWeekCounts.indexOf(Math.max(...dayOfWeekCounts));
  const bestDay    = dayOfWeekNames[bestDayIdx];

  // ── Gesamtzahl abgehakte Habits ──────────────
  const totalChecks = rows.reduce((n, r) => n + (r.done_habits?.length || 0), 0);

  return {
    last7,
    completionRate,
    bestStreak,
    habitStreaks,
    habitStats,
    topHabit,
    missedHabit,
    bestDay,
    totalChecks,
    activeDays: activeDays.length,
    completedDays: completedDays.length,
  };
}

// ─────────────────────────────────────────────
// EXPORTIERTER HELPER für Today-Tab
// ─────────────────────────────────────────────
export function computeHabitStreaksFromHistory(history, allHabits) {
  const today = new Date().toISOString().slice(0, 10);
  const habitStreaks = {};

  for (const habit of allHabits) {
    let streak = 0;
    // Rückwärts durch die letzten 60 Tage
    for (let i = 0; i < 60; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const row = history[key];
      if (!row) { if (i === 0) continue; else break; }
      if ((row.done_habits || []).includes(habit.id)) {
        streak++;
      } else if (key !== today) {
        break;
      }
    }
    habitStreaks[habit.id] = streak;
  }

  return habitStreaks;
}